import React, { useState } from "react";
import { ShoppingCart, Plus, Trash2, Calculator, Check, Building2, AlertTriangle, ShieldCheck } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { useNotification } from "../../app/context/NotificationContext";
import { formatCurrency } from "../../utils/formatters";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { RetailPOS } from "./RetailPOS";
import { ReceiptModal } from "./ReceiptModal";

export function WholesaleSale() {
  const { products, customers, processSale } = useApp();
  const { addToast } = useNotification();

  const wholesaleCustomers = customers.filter((c) => c.type === "Wholesale");
  const [selectedCustomerId, setSelectedCustomerId] = useState(wholesaleCustomers[0]?.id || "");
  const [paymentTerms, setPaymentTerms] = useState("Bank NEFT (Credit 21d)");
  const [paymentStatus, setPaymentStatus] = useState("Pending");
  const [notes, setNotes] = useState("Direct commercial delivery to client shop/hotel kitchen.");
  const [lastCompletedOrder, setLastCompletedOrder] = useState(null);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId) || wholesaleCustomers[0];

  const [lines, setLines] = useState([
    {
      productId: products[0]?.id || "",
      productName: products[0]?.name || "",
      qty: 20,
      costPrice: products[0]?.purchasePrice || 360,
      sellingPrice: products[0]?.wholesalePrice || 400,
      mrp: products[0]?.MRP || 480,
      discount: 0,
      total: (products[0]?.wholesalePrice || 400) * 20,
      profit: ((products[0]?.wholesalePrice || 400) - (products[0]?.purchasePrice || 360)) * 20
    }
  ]);

  const handleProductChange = (index, prodId) => {
    const prod = products.find((p) => p.id === prodId);
    if (!prod) return;
    const updated = [...lines];
    const qty = updated[index].qty || 1;
    updated[index] = {
      ...updated[index],
      productId: prod.id,
      productName: prod.name,
      costPrice: prod.purchasePrice,
      sellingPrice: prod.wholesalePrice,
      mrp: prod.MRP,
      total: prod.wholesalePrice * qty,
      profit: (prod.wholesalePrice - prod.purchasePrice) * qty
    };
    setLines(updated);
  };

  const handleQtyChange = (index, qty) => {
    const updated = [...lines];
    const num = Math.max(1, Number(qty) || 1);
    updated[index].qty = num;
    updated[index].total = updated[index].sellingPrice * num;
    updated[index].profit = (updated[index].sellingPrice - updated[index].costPrice) * num;
    setLines(updated);
  };

  const handlePriceChange = (index, price) => {
    const updated = [...lines];
    const num = Math.max(0, Number(price) || 0);
    updated[index].sellingPrice = num;
    updated[index].total = num * updated[index].qty;
    updated[index].profit = (num - updated[index].costPrice) * updated[index].qty;
    setLines(updated);
  };

  const addRow = () => {
    const defaultProd = products[0];
    setLines([
      ...lines,
      {
        productId: defaultProd?.id || "",
        productName: defaultProd?.name || "",
        qty: 10,
        costPrice: defaultProd?.purchasePrice || 100,
        sellingPrice: defaultProd?.wholesalePrice || 120,
        mrp: defaultProd?.MRP || 150,
        discount: 0,
        total: (defaultProd?.wholesalePrice || 120) * 10,
        profit: ((defaultProd?.wholesalePrice || 120) - (defaultProd?.purchasePrice || 100)) * 10
      }
    ]);
  };

  const removeRow = (index) => {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== index));
  };

  const subtotal = lines.reduce((sum, l) => sum + l.total, 0);
  const totalCost = lines.reduce((sum, l) => sum + l.costPrice * l.qty, 0);
  const taxGst = Math.round(subtotal * 0.05); // 5% GST
  const grandTotal = subtotal + taxGst;
  const totalProfit = grandTotal - totalCost;
  const marginPercentage = grandTotal > 0 ? Number(((totalProfit / grandTotal) * 100).toFixed(2)) : 0;

  // Credit Limit Check
  const newOutstanding = (selectedCustomer?.outstandingBalance || 0) + grandTotal;
  const isCreditExceeded = selectedCustomer && newOutstanding > selectedCustomer.creditLimit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const order = await processSale({
        channel: "Wholesale",
        customerId: selectedCustomerId,
        customerName: selectedCustomer?.name,
        cashierName: "Rameshwar Gurav (Sales Team)",
        paymentMethod: paymentTerms,
        paymentStatus,
        items: lines,
        subtotal,
        taxGst,
        discountAmount: 0,
        grandTotal,
        totalProfit,
        marginPercentage,
        notes
      });

      setLastCompletedOrder(order);
      setIsReceiptOpen(true);
      addToast(`Wholesale Invoice created successfully for ${selectedCustomer?.name}!`, "success");
    } catch (err) {
      addToast("Failed to process wholesale order.", "error");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 text-xs">
      {/* Customer & Credit Term Header */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            B2B Wholesale Buyer / Client *
          </label>
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            {wholesaleCustomers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.city})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Credit & Payment Terms *
          </label>
          <select
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          >
            <option value="Bank NEFT (Credit 21d)">Bank NEFT (Credit 21 Days)</option>
            <option value="Cheque / Clearing (Net 30d)">Cheque / Clearing (Net 30 Days)</option>
            <option value="Immediate RTGS Settlement">Immediate RTGS Settlement</option>
            <option value="Cash on Delivery (COD)">Cash on Delivery (COD)</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Settlement Status *
          </label>
          <select
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-semibold text-emerald-600"
          >
            <option value="Pending">Pending (Post to Account Ledger)</option>
            <option value="Paid">Paid (Immediate Bank Transfer)</option>
          </select>
        </div>
      </div>

      {/* Credit Limit Alert Banner */}
      {selectedCustomer && (
        <div
          className={`p-3.5 rounded-2xl border text-xs flex items-center justify-between ${
            isCreditExceeded
              ? "bg-rose-50 dark:bg-rose-950/30 border-rose-300 dark:border-rose-900 text-rose-800 dark:text-rose-300"
              : "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-300"
          }`}
        >
          <div className="flex items-center gap-2">
            {isCreditExceeded ? <AlertTriangle className="w-4 h-4 text-rose-600" /> : <ShieldCheck className="w-4 h-4 text-emerald-600" />}
            <div>
              <span className="font-bold">
                Credit Limit: {formatCurrency(selectedCustomer.creditLimit)} • Current Outstanding: {formatCurrency(selectedCustomer.outstandingBalance)}
              </span>
              <p className="text-[11px] opacity-80">
                Projected Balance after this order: {formatCurrency(newOutstanding)}
              </p>
            </div>
          </div>
          {isCreditExceeded && (
            <span className="bg-rose-600 text-white font-bold px-2 py-0.5 rounded text-[10px]">
              Credit Limit Warning
            </span>
          )}
        </div>
      )}

      {/* Bulk Line Items Grid */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 bg-white dark:bg-slate-900 space-y-3 shadow-xs">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5">
            <Calculator className="w-4 h-4 text-emerald-500" />
            Wholesale Bulk Line Items ({lines.length})
          </h4>
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-lg shadow-sm"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Wholesale SKU
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider font-semibold border-b border-slate-200 dark:border-slate-800 text-[10px]">
                <th className="py-2 px-2">Product</th>
                <th className="py-2 px-2 w-24">Bulk Qty</th>
                <th className="py-2 px-2 w-28">Cost Price (₹)</th>
                <th className="py-2 px-2 w-28">Negotiated Price (₹)</th>
                <th className="py-2 px-2 text-right">Profit Line</th>
                <th className="py-2 px-2 text-right">Line Total</th>
                <th className="py-2 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/60 dark:divide-slate-800">
              {lines.map((row, idx) => (
                <tr key={idx}>
                  <td className="py-2 px-2">
                    <select
                      value={row.productId}
                      onChange={(e) => handleProductChange(idx, e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
                    >
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.unit})
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min="1"
                      value={row.qty}
                      onChange={(e) => handleQtyChange(idx, e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg font-mono font-bold"
                    />
                  </td>
                  <td className="py-2 px-2 font-mono text-slate-400">
                    ₹{row.costPrice}
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min="0"
                      value={row.sellingPrice}
                      onChange={(e) => handlePriceChange(idx, e.target.value)}
                      className="w-full px-2 py-1.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg font-mono font-bold text-sky-600 dark:text-sky-400"
                    />
                  </td>
                  <td className="py-2 px-2 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    +{formatCurrency(row.profit)}
                  </td>
                  <td className="py-2 px-2 text-right font-mono font-bold text-slate-900 dark:text-white">
                    {formatCurrency(row.total)}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      disabled={lines.length <= 1}
                      className="text-slate-400 hover:text-rose-500 disabled:opacity-30"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wholesale Financial Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-slate-100 dark:bg-slate-950 p-4 rounded-2xl border border-slate-200 dark:border-slate-800">
        <div>
          <span className="text-slate-400">Subtotal:</span>
          <p className="text-base font-bold font-mono text-slate-800 dark:text-slate-200">{formatCurrency(subtotal)}</p>
        </div>
        <div>
          <span className="text-slate-400">GST (5% Tax):</span>
          <p className="text-base font-bold font-mono text-slate-800 dark:text-slate-200">{formatCurrency(taxGst)}</p>
        </div>
        <div>
          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Total Margin & Profit:</span>
          <p className="text-base font-bold font-mono text-emerald-600 dark:text-emerald-400">
            +{formatCurrency(totalProfit)} ({marginPercentage}%)
          </p>
        </div>
        <div className="text-right">
          <span className="text-slate-400 font-semibold">Grand Total:</span>
          <p className="text-xl font-extrabold font-mono text-slate-900 dark:text-white">
            {formatCurrency(grandTotal)}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2.5">
        <button
          type="submit"
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-600/30 text-xs"
        >
          Issue Wholesale Invoice & Post to Ledger
        </button>
      </div>

      <ReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        order={lastCompletedOrder}
      />
    </form>
  );
}

export function SalesModule() {
  const { salesOrders } = useApp();
  const [activeTab, setActiveTab] = useState("retail"); // 'retail' | 'wholesale' | 'invoices'

  const columns = [
    {
      header: "Invoice No",
      accessorKey: "orderNumber",
      render: (val, row) => (
        <div>
          <span className="font-bold font-mono text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">{row.channel} Sale</p>
        </div>
      )
    },
    {
      header: "Customer",
      accessorKey: "customerName",
      render: (val) => <span className="font-medium text-slate-800 dark:text-slate-200">{val}</span>
    },
    {
      header: "Payment Method",
      accessorKey: "paymentMethod",
      render: (val) => <span className="font-mono text-slate-600 dark:text-slate-400">{val}</span>
    },
    {
      header: "Profit Line (₹)",
      accessorKey: "totalProfit",
      align: "right",
      render: (val, row) => (
        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
          +{formatCurrency(val)} ({row.marginPercentage || 12}%)
        </span>
      )
    },
    {
      header: "Grand Total (₹)",
      accessorKey: "grandTotal",
      align: "right",
      render: (val) => (
        <span className="font-bold font-mono text-slate-900 dark:text-white">
          {formatCurrency(val)}
        </span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-emerald-500" />
            Dual Sales Engine (Retail Walk-in POS & Wholesale B2B)
          </h2>
          <p className="text-xs text-slate-500">
            Rapid cashier POS counter with thermal receipt printing + wholesale negotiated credit desk
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
          <button
            onClick={() => setActiveTab("retail")}
            className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
              activeTab === "retail" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Retail POS Counter
          </button>
          <button
            onClick={() => setActiveTab("wholesale")}
            className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
              activeTab === "wholesale" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Wholesale B2B Bulk Desk
          </button>
          <button
            onClick={() => setActiveTab("invoices")}
            className={`px-3.5 py-1.5 rounded-lg font-semibold transition ${
              activeTab === "invoices" ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-xs" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            Sales Register ({salesOrders.length})
          </button>
        </div>
      </div>

      {activeTab === "retail" && <RetailPOS />}
      {activeTab === "wholesale" && <WholesaleSale />}
      {activeTab === "invoices" && (
        <DataTable
          columns={columns}
          data={salesOrders}
          title="Sales Orders & Invoices Master Ledger"
          subtitle="All completed retail walk-ins and wholesale orders with margin analysis"
          filename="kirana_sales_invoices"
        />
      )}
    </div>
  );
}
